import { enumArray, optionalString, stringArray } from '~/lib/search-params'
import { PRIORITY_ORDER } from '~/lib/task-display'
import { DUE_ORDER, type TaskDueFilter } from '~/lib/task-due'
import type { TaskPriority, TaskStatus, TaskView } from '~/lib/tasks'
import type { Id } from '~convex/_generated/dataModel'
import {
  TASK_VIEW_FIELDS,
  TASK_VIEW_GROUP_BYS,
  TASK_VIEW_SORT_KEYS,
  type TaskViewField,
  type TaskViewGroupBy,
  type TaskViewSort,
} from '~convex/lib/taskViews'

/**
 * The task board's filter state, mirrored into the URL so it survives a
 * refresh. Owned by `<TaskBoard/>` on the workspace tasks page. The shape and
 * decoder stay in this leaf module because routed links — including the
 * retired project Tasks URL — need the same canonical field, sort, and
 * grouping whitelists imported below.
 */
export type TasksSearch = {
  /** The selected view; absent falls back to the persisted choice. */
  view?: TaskView
  sprints?: Id<'sprints'>[]
  projects?: Id<'projects'>[]
  assignees?: Id<'users'>[]
  statuses?: TaskStatus[]
  priorities?: TaskPriority[]
  labels?: Id<'labels'>[]
  due?: TaskDueFilter[]
  /** Active personal task view. Its config is mirrored into the URL. */
  viewId?: Id<'taskViews'>
  group?: TaskViewGroupBy
  sort?: TaskViewSort['key']
  direction?: TaskViewSort['direction']
  fields?: TaskViewField[]
  /** Local title/project/assignee search. Not persisted into a view. */
  q?: string
}

export const isTaskView = (raw: unknown): raw is TaskView =>
  raw === 'list' || raw === 'board' || raw === 'backlog' || raw === 'gantt'

const isTaskViewGroupBy = (raw: unknown): raw is TaskViewGroupBy =>
  TASK_VIEW_GROUP_BYS.some((groupBy) => groupBy === raw)

const isTaskViewSortKey = (raw: unknown): raw is TaskViewSort['key'] =>
  TASK_VIEW_SORT_KEYS.some((key) => key === raw)

/** Whitelisted decoder for the task-board search shape — drops unknowns silently. */
export const decodeTasksSearch = (search: Record<string, unknown>): TasksSearch => ({
  assignees: stringArray<Id<'users'>>(search.assignees),
  direction:
    search.direction === 'asc' || search.direction === 'desc' ? search.direction : undefined,
  due: enumArray<TaskDueFilter>(search.due, DUE_ORDER),
  fields: enumArray<TaskViewField>(search.fields, TASK_VIEW_FIELDS),
  group: isTaskViewGroupBy(search.group) ? search.group : undefined,
  labels: stringArray<Id<'labels'>>(search.labels),
  priorities: enumArray<TaskPriority>(search.priorities, PRIORITY_ORDER),
  projects: stringArray<Id<'projects'>>(search.projects),
  q: optionalString(search.q),
  sort: isTaskViewSortKey(search.sort) ? search.sort : undefined,
  sprints: stringArray<Id<'sprints'>>(search.sprints),
  // Status keys are workspace-defined (custom statuses), so there is no
  // static whitelist — a stale/foreign key in the URL just matches no tasks.
  statuses: stringArray<TaskStatus>(search.statuses),
  view: isTaskView(search.view) ? search.view : undefined,
  viewId: optionalString<Id<'taskViews'>>(search.viewId),
})
